import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Modal, Space } from 'antd';
import { MailOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../config/api';
import toast from 'react-hot-toast';

const { Title, Text, Link } = Typography;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  // Use hardcoded logo to prevent removal issues - embedded as data URL
  const logoUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMjAwIDgwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogIDx0ZXh0IHg9IjEwMCIgeT0iMzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJzZXJpZiIgZm9udC1zaXplPSIyNCIgZm9udC13ZWlnaHQ9Im5vcm1hbCIgZmlsbD0iIzAwMDAwMCI+UEFURUw8L3RleHQ+CiAgPHRleHQgeD0iMTAwIiB5PSI2NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMzIiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSIjMDA3YmZmIj5IUk1TPC90ZXh0Pgo8L3N2Zz4=';

  const handleSubmit = async (values: any) => {
    setError('');
    setLoading(true);

    try {
      await login(values.email, values.password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Login failed';
      const errorCode = err.response?.data?.code;
      
      let displayError = '';
      
      // Show specific error messages based on error type
      if (errorCode === 'INVALID_CREDENTIALS' || errorMessage.includes('Invalid email or password')) {
        displayError = 'Invalid email or password. Please check your credentials and try again.';
      } else if (errorCode === 'ACCOUNT_DEACTIVATED' || errorMessage.includes('deactivated')) {
        displayError = 'Your account has been deactivated. Please contact an administrator.';
      } else if (errorMessage.includes('email')) {
        displayError = 'Invalid email format. Please enter a valid email address.';
      } else {
        displayError = errorMessage;
      }
      
      setError(displayError);
      toast.error(displayError);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values: any) => {
    setForgotPasswordError('');
    setForgotPasswordLoading(true);

    try {
      const response = await api.post('/api/auth/request-password-reset', {
        email: values.email,
      });
      
      // Check if email was actually sent
      const emailSent = response.data?.emailSent;
      const message = response.data?.message || 'Password reset link sent to your email';
      
      if (emailSent === false) {
        // Email was not sent (likely disabled or not configured)
        toast('Password reset token generated, but email could not be sent. Please check server logs for the reset link or contact an administrator.', {
          icon: '⚠️',
          duration: 5000,
        });
        setForgotPasswordError('Email notifications are disabled or SMTP is not configured. Please contact an administrator for assistance.');
      } else {
        toast.success(message);
        setForgotPasswordOpen(false);
        forgotPasswordForm.resetFields();
      }
    } catch (error: any) {
      console.error('Password reset request error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send password reset email';
      setForgotPasswordError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const openForgotPassword = () => {
    const currentEmail = form.getFieldValue('email');
    if (currentEmail) {
      forgotPasswordForm.setFieldsValue({ email: currentEmail });
    }
    setForgotPasswordError('');
    setForgotPasswordOpen(true);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 50%, #0D47A1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
      }}
    >
      {/* Background overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(30, 136, 229, 0.25) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.12) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ width: '100%', maxWidth: 1000, position: 'relative', zIndex: 1 }}>
        <Card
          style={{
            boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
            borderRadius: 16,
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', minHeight: 600 }}>
            {/* Left Side - Logo */}
            <div
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 40px',
                position: 'relative',
              }}
            >
              {/* Logo */}
              <div
                style={{
                  width: 200,
                  height: 200,
                  borderRadius: 24,
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 32,
                  boxShadow: '0px 16px 32px rgba(0, 0, 0, 0.2)',
                  padding: 20,
                  overflow: 'hidden',
                }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <Title
                    level={1}
                    style={{
                      color: '#1976d2',
                      fontWeight: 'bold',
                      fontSize: '5rem',
                      margin: 0,
                    }}
                  >
                    P
                  </Title>
                )}
              </div>
              
              {/* Branding Text */}
              <div style={{ textAlign: 'center' }}>
                <Title
                  level={2}
                  style={{
                    color: 'white',
                    fontWeight: 600,
                    marginBottom: 8,
                    fontSize: '2rem',
                  }}
                >
                  {/* Patel HRMS */}
                </Title>
                <Text style={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.9)' }}>
                  Powered by Patel Processing ❤️
                </Text>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div
              style={{
                flex: 1,
                padding: '60px 40px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {/* Welcome Text */}
              <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <Title
                  level={2}
                  style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    color: '#1976d2',
                  }}
                >
                  Welcome Back
                </Title>
                <Text style={{ fontSize: 16, color: '#666' }}>
                  Sign in to your account
                </Text>
              </div>

          {/* Error Alert */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ marginBottom: 24 }}
            />
          )}

          {/* Login Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#1976d2' }} />}
                placeholder="Email Address"
                style={{
                  borderRadius: 8,
                  backgroundColor: 'rgba(103, 80, 164, 0.02)',
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#1976d2' }} />}
                placeholder="Password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                style={{
                  borderRadius: 8,
                  backgroundColor: 'rgba(103, 80, 164, 0.02)',
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #6750A4 0%, #4F378B 100%)',
                  border: 'none',
                  boxShadow: '0px 4px 12px rgba(103, 80, 164, 0.4)',
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Form.Item>

            {/* Forgot Password Link */}
            <div style={{ textAlign: 'center' }}>
              <Link onClick={openForgotPassword} style={{ color: '#1976d2' }}>
                Forgot your password?
              </Link>
            </div>
          </Form>
            </div>
          </div>
        </Card>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        title="Reset Password"
        open={forgotPasswordOpen}
        onCancel={() => {
          setForgotPasswordOpen(false);
          forgotPasswordForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Enter your email address and we'll send you a link to reset your password.
        </Text>

        {forgotPasswordError && (
          <Alert
            message={forgotPasswordError}
            type="error"
            showIcon
            closable
            onClose={() => setForgotPasswordError('')}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={forgotPasswordForm}
          layout="vertical"
          onFinish={handleForgotPassword}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: '#1976d2' }} />}
              placeholder="Email Address"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setForgotPasswordOpen(false);
                  forgotPasswordForm.resetFields();
                }}
                disabled={forgotPasswordLoading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={forgotPasswordLoading}
                style={{
                  background: 'linear-gradient(135deg, #6750A4 0%, #4F378B 100%)',
                  border: 'none',
                }}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
